import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from './firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import './AdminDashboard.css';
import { useAdminView } from './contexts/AdminViewContext'; // Use admin view context

const getAllGenerationsForAdmin = httpsCallable(functions, 'getAllGenerationsForAdmin');
const getGenerationDetailsForAdmin = httpsCallable(functions, 'getGenerationDetailsForAdmin');
const searchUsers = httpsCallable(functions, 'searchUsers');
const updateUserMonthlyLimit = httpsCallable(functions, 'updateUserMonthlyLimit');
const searchGenerationsByActivity = httpsCallable(functions, 'searchGenerationsByActivity');
const adminDeleteUserAccount = httpsCallable(functions, 'adminDeleteUserAccount');
const resetAllUserCredits = httpsCallable(functions, 'resetAllUserCredits');
const getUserBadges = httpsCallable(functions, 'getUserBadges');

// User Limit Editor Component
function UserLimitEditor({ userId, currentLimit, defaultLimit, onUpdateLimit, isUpdating }) {
    const [newLimit, setNewLimit] = useState(currentLimit || '');
    const [isEditing, setIsEditing] = useState(false);

    const handleSave = () => {
        if (newLimit === currentLimit) {
            setIsEditing(false);
            return;
        }
        
        onUpdateLimit(userId, newLimit);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setNewLimit(currentLimit || '');
        setIsEditing(false);
    };

    const handleReset = () => {
        setNewLimit('');
        onUpdateLimit(userId, '');
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="limit-editor">
                <div className="editor-input">
                    <input
                        type="number"
                        min="0"
                        value={newLimit}
                        onChange={(e) => setNewLimit(e.target.value)}
                        placeholder={`Default: ${defaultLimit}`}
                        disabled={isUpdating}
                        className="limit-input"
                    />
                </div>
                <div className="editor-buttons">
                    <button 
                        onClick={handleSave}
                        disabled={isUpdating}
                        className="save-button"
                    >
                        {isUpdating ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                        onClick={handleCancel}
                        disabled={isUpdating}
                        className="cancel-button"
                    >
                        Cancel
                    </button>
                    {currentLimit !== null && (
                        <button 
                            onClick={handleReset}
                            disabled={isUpdating}
                            className="reset-button"
                            title="Reset to default limit"
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="limit-display">
            <button 
                onClick={() => setIsEditing(true)}
                className="edit-limit-button"
                disabled={isUpdating}
            >
                {currentLimit !== null ? `Edit (${currentLimit})` : 'Set Custom Limit'}
            </button>
        </div>
    );
}

function AdminDashboard({ user }) {
    const { isAdmin } = useAdminView(); // Use context for admin status
    const [generations, setGenerations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [detailedGenerations, setDetailedGenerations] = useState(new Map()); // Store detailed data for expanded rows
    const [fetchingDetails, setFetchingDetails] = useState(new Set()); // Track which generations are being fetched
    const [expandedPrompts, setExpandedPrompts] = useState(new Set()); // Track which prompts are expanded
    const [listeningToRealtime, setListeningToRealtime] = useState(false);
    const generationListenersRef = useRef(new Map());
    const mainListenerRef = useRef(null);
    const [newGenerationIds, setNewGenerationIds] = useState(new Set());
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Badge state
    const [generationBadges, setGenerationBadges] = useState(new Map());
    const [loadingBadges, setLoadingBadges] = useState(new Set());
    const [selectedBadge, setSelectedBadge] = useState(null);
    
    // Activity search state
    const [activitySearchTerm, setActivitySearchTerm] = useState('');
    const [filteredGenerations, setFilteredGenerations] = useState([]);
    const [showTestGenerations, setShowTestGenerations] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [searchResults, setSearchResults] = useState(null);
    const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'admin', 'normal'
    
    // Advanced search state
    const [searchType, setSearchType] = useState('activityName');
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minValue, setMinValue] = useState('');
    const [maxValue, setMaxValue] = useState('');

    // User management state
    const [activeTab, setActiveTab] = useState('generations'); // 'generations' or 'users'
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const [userSearchError, setUserSearchError] = useState(null);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    
    // User deletion state
    const [deletingUser, setDeletingUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingUser, setIsDeletingUser] = useState(false);
    
    // Credit reset state
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetConfirmText, setResetConfirmText] = useState('');
    const [isResettingCredits, setIsResettingCredits] = useState(false);
    const [resetResult, setResetResult] = useState(null);
    
    // Template names state
    const [templateNames, setTemplateNames] = useState(new Map());


    // Function to fetch template names from Firestore
    const fetchTemplateNames = async (templateIds) => {
        const newTemplateNames = new Map(templateNames);
        const idsToFetch = templateIds.filter(id => id && !newTemplateNames.has(id));
        
        if (idsToFetch.length === 0) return;
        
        try {
            const promises = idsToFetch.map(async (templateId) => {
                try {
                    const templateDoc = await getDoc(doc(db, 'promptTemplates', templateId));
                    if (templateDoc.exists()) {
                        return [templateId, templateDoc.data().name || templateId];
                    }
                    return [templateId, templateId]; // Fallback to ID if doc doesn't exist
                } catch (err) {
                    console.error(`Error fetching template ${templateId}:`, err);
                    return [templateId, templateId]; // Fallback to ID on error
                }
            });
            
            const results = await Promise.all(promises);
            results.forEach(([id, name]) => {
                newTemplateNames.set(id, name);
            });
            
            setTemplateNames(newTemplateNames);
        } catch (error) {
            console.error('Error fetching template names:', error);
        }
    };

    const fetchGenerations = async (page = 1, newSortBy = sortBy, newSortOrder = sortOrder) => {
        if (!user || !isAdmin) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await getAllGenerationsForAdmin({
                page,
                limit: pagination.limit,
                sortBy: newSortBy,
                sortOrder: newSortOrder,
                userTypeFilter: userTypeFilter,
                showTestGenerations: showTestGenerations
            });

            const fetchedGenerations = result.data.generations;
            setGenerations(fetchedGenerations);
            setPagination(result.data.pagination);

            // Fetch template names for all generations with template IDs
            const templateIds = fetchedGenerations
                .map(gen => gen.promptTemplateId)
                .filter(id => id && id !== '');
            if (templateIds.length > 0) {
                fetchTemplateNames(templateIds);
            }

            // Fetch badges for all generations by default
            fetchBadgesForGenerations(fetchedGenerations);


            // Set up listeners for all fetched generations
            fetchedGenerations.forEach(generation => {
                setupDocumentListener(generation.id);
            });
            
            // Mark initial load as complete after first fetch
            if (isInitialLoad) {
                setTimeout(() => setIsInitialLoad(false), 1000);
            }
        } catch (err) {
            console.error("Error fetching generations:", err);
            setError(err.message || 'Failed to fetch generations');
        } finally {
            setIsLoading(false);
        }
    };

    // User search functionality
    const handleUserSearch = async () => {
        if (!userSearchTerm || userSearchTerm.trim().length < 2) {
            setUserSearchError('Search term must be at least 2 characters long');
            return;
        }

        setIsSearchingUsers(true);
        setUserSearchError(null);

        try {
            const result = await searchUsers({
                searchTerm: userSearchTerm.trim(),
                limit: 20
            });

            setSearchedUsers(result.data.users);
            if (result.data.users.length === 0) {
                setUserSearchError('No users found matching your search');
            }
        } catch (err) {
            console.error("Error searching users:", err);
            setUserSearchError(err.message || 'Failed to search users');
            setSearchedUsers([]);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    // Update user monthly limit
    const handleUpdateUserLimit = async (targetUserId, newLimit) => {
        setUpdatingUserId(targetUserId);

        try {
            const result = await updateUserMonthlyLimit({
                targetUserId,
                newLimit: newLimit === '' ? null : parseInt(newLimit)
            });

            if (result.data.success) {
                // Update the local user data
                setSearchedUsers(prev => prev.map(u => 
                    u.uid === targetUserId 
                        ? {
                            ...u,
                            monthlyGenerations: {
                                ...u.monthlyGenerations,
                                customLimit: result.data.newLimit,
                                effectiveLimit: result.data.effectiveLimit
                            }
                        }
                        : u
                ));
                alert(`Successfully updated monthly limit for user to ${result.data.effectiveLimit}`);
            }
        } catch (err) {
            console.error("Error updating user limit:", err);
            alert(`Failed to update user limit: ${err.message}`);
        } finally {
            setUpdatingUserId(null);
        }
    };

    // Delete user handler
    const handleDeleteUser = async () => {
        if (!deletingUser || deleteConfirmText !== 'DELETE') return;

        setIsDeletingUser(true);

        try {
            const result = await adminDeleteUserAccount({
                targetUserId: deletingUser.uid
            });

            if (result.data.success) {
                // Remove the user from the search results
                setSearchedUsers(prev => prev.filter(u => u.uid !== deletingUser.uid));
                
                alert(`Successfully deleted user account for ${result.data.deletedUser.displayName || deletingUser.email}`);
                
                // Reset modal state
                setShowDeleteModal(false);
                setDeletingUser(null);
                setDeleteConfirmText('');
            }
        } catch (err) {
            console.error("Error deleting user:", err);
            alert(`Failed to delete user: ${err.message}`);
        } finally {
            setIsDeletingUser(false);
        }
    };

    // Reset all credits handler
    const handleResetAllCredits = async () => {
        if (resetConfirmText !== 'RESET ALL') return;

        setIsResettingCredits(true);

        try {
            const result = await resetAllUserCredits();

            if (result.data.success) {
                setResetResult(result.data);
                
                // Refresh user search results if any
                if (searchedUsers.length > 0) {
                    await handleUserSearch();
                }
            }
        } catch (err) {
            console.error("Error resetting all credits:", err);
            alert(`Failed to reset credits: ${err.message}`);
        } finally {
            setIsResettingCredits(false);
        }
    };

    // Activity search functionality
    const handleActivitySearch = async (page = 1) => {
        // Validate search parameters based on search type
        if (searchType === 'activityName' && (!activitySearchTerm || activitySearchTerm.trim().length < 2)) {
            setSearchError('Search term must be at least 2 characters long');
            return;
        }
        
        if (searchType === 'dateRange' && (!startDate || !endDate)) {
            setSearchError('Please select both start and end dates');
            return;
        }
        
        if (['power', 'distance', 'elevation'].includes(searchType) && !minValue) {
            setSearchError('Please enter a minimum value');
            return;
        }

        setIsSearching(true);
        setSearchError(null);
        setIsSearchMode(true);

        try {
            const searchParams = {
                searchType,
                page: page,
                limit: pagination.limit
            };
            
            // Add parameters based on search type
            if (searchType === 'activityName' || searchType === 'activityType') {
                searchParams.searchTerm = activitySearchTerm.trim();
            } else if (searchType === 'dateRange') {
                searchParams.startDate = startDate;
                searchParams.endDate = endDate;
            } else if (['power', 'distance', 'elevation'].includes(searchType)) {
                searchParams.minValue = parseFloat(minValue);
                if (maxValue) {
                    searchParams.maxValue = parseFloat(maxValue);
                }
            }
            
            const result = await searchGenerationsByActivity(searchParams);

            setSearchResults(result.data);
            setGenerations(result.data.generations);
            setPagination(result.data.pagination);
            
            // Fetch template names for search results too
            const templateIds = result.data.generations
                .map(gen => gen.promptTemplateId)
                .filter(id => id && id !== '');
            if (templateIds.length > 0) {
                fetchTemplateNames(templateIds);
            }

            
            if (result.data.generations.length === 0) {
                setSearchError('No activities found matching your search criteria');
            }
        } catch (err) {
            console.error("Error searching activities:", err);
            setSearchError(err.message || 'Failed to search activities');
        } finally {
            setIsSearching(false);
        }
    };

    const clearActivitySearch = () => {
        setActivitySearchTerm('');
        setIsSearchMode(false);
        setSearchResults(null);
        setSearchError(null);
        setSearchType('activityName');
        setStartDate('');
        setEndDate('');
        setMinValue('');
        setMaxValue('');
        setShowAdvancedSearch(false);
        // Don't reset userTypeFilter and showTestGenerations here as they are persistent filters
        fetchGenerations(1); // Reset to normal view
    };

    // TEMPORARY: Initialize system test user

    // Helper function to fetch complete generation data from server
    const fetchGenerationDetails = async (generationId) => {
        try {
            const result = await getGenerationDetailsForAdmin({ generationId });
            return result.data;
        } catch (error) {
            console.error(`Error fetching generation details for ${generationId}:`, error);
            throw error;
        }
    };

    // Fetch badges for a generation
    const fetchGenerationBadges = async (generationId) => {
        if (loadingBadges.has(generationId) || generationBadges.has(generationId)) {
            return;
        }

        setLoadingBadges(prev => new Set([...prev, generationId]));
        
        try {
            const result = await getUserBadges({ generationRequestId: generationId });
            console.log(`[Admin Dashboard] Badges received for generation ${generationId}:`, result.data.badges);
            setGenerationBadges(prev => new Map([...prev, [generationId, result.data.badges || []]]));
        } catch (error) {
            console.error(`Error fetching badges for generation ${generationId}:`, error);
            setGenerationBadges(prev => new Map([...prev, [generationId, []]]));
        } finally {
            setLoadingBadges(prev => {
                const newSet = new Set(prev);
                newSet.delete(generationId);
                return newSet;
            });
        }
    };

    // Fetch badges for multiple generations
    const fetchBadgesForGenerations = async (generations) => {
        const generationsWithStravaId = generations.filter(gen => gen.stravaId && !generationBadges.has(gen.id));
        
        // Fetch badges in parallel but with a limit to avoid overwhelming the server
        const batchSize = 10;
        for (let i = 0; i < generationsWithStravaId.length; i += batchSize) {
            const batch = generationsWithStravaId.slice(i, i + batchSize);
            await Promise.all(batch.map(gen => fetchGenerationBadges(gen.id)));
        }
    };

    // Set up real-time listeners for new and updated generations
    const setupRealtimeListeners = () => {
        if (!isAdmin || listeningToRealtime) return;

        setListeningToRealtime(true);

        // Listen for new generations (most recent)
        const q = query(
            collection(db, 'GenerationRequests'),
            orderBy('createdAt', 'desc'),
            limit(10)
        );

        mainListenerRef.current = onSnapshot(q, async (snapshot) => {
            const changes = snapshot.docChanges();
            
            for (const change of changes) {
                const docId = change.doc.id;

                if (change.type === 'added') {
                    // Check if we already have this generation
                    const exists = generations.some(g => g.id === docId);
                    if (!exists) {
                        // Fetch complete generation details from server
                        try {
                            const generationData = await fetchGenerationDetails(docId);
                            
                            // Add to the top of the list
                            setGenerations(prev => {
                                const alreadyExists = prev.some(g => g.id === docId);
                                if (!alreadyExists) {
                                    // Add individual listener for this document
                                    setupDocumentListener(docId);
                                    
                                    // Fetch badges for the new generation if it has a stravaId
                                    if (generationData.stravaId) {
                                        fetchGenerationBadges(generationData.id);
                                    }
                                    
                                    // Mark as new if not initial load
                                    if (!isInitialLoad) {
                                        setNewGenerationIds(prevIds => new Set([...prevIds, docId]));
                                        // Remove the highlight after 5 seconds
                                        setTimeout(() => {
                                            setNewGenerationIds(prevIds => {
                                                const newIds = new Set(prevIds);
                                                newIds.delete(docId);
                                                return newIds;
                                            });
                                        }, 5000);
                                    }
                                    
                                    return [generationData, ...prev].slice(0, pagination.limit);
                                }
                                return prev;
                            });
                        } catch (error) {
                            console.error('Error fetching new generation details:', error);
                        }
                    }
                }
            }
        });
    };

    // Set up listener for individual document updates
    const setupDocumentListener = (docId) => {
        if (generationListenersRef.current.has(docId)) return;

        const unsubscribe = onSnapshot(doc(db, 'GenerationRequests', docId), async (docSnapshot) => {
            if (docSnapshot.exists()) {
                try {
                    // Fetch complete generation details from server
                    const generationData = await fetchGenerationDetails(docId);
                    
                    setGenerations(prev => prev.map(g => 
                        g.id === docId ? generationData : g
                    ));
                } catch (error) {
                    console.error('Error updating generation:', error);
                }
            }
        });

        generationListenersRef.current.set(docId, unsubscribe);
    };

    // Filter generations based on activity search term (other filters are handled server-side)
    useEffect(() => {
        let filtered = generations;
        
        // Filter by search term (local filtering for current page only)
        if (activitySearchTerm && !isSearchMode) {
            const searchLower = activitySearchTerm.toLowerCase();
            filtered = filtered.filter(generation => {
                // Check if stravaStats exists and has activityName
                const activityName = generation.stravaStats?.activityName || '';
                return activityName.toLowerCase().includes(searchLower);
            });
        }
        
        setFilteredGenerations(filtered);
    }, [activitySearchTerm, generations, isSearchMode]);

    // Initial load and setup listeners
    useEffect(() => {
        if (isAdmin) {
            fetchGenerations();
            setupRealtimeListeners();
        }

        // Cleanup function
        return () => {
            // Unsubscribe from main listener
            if (mainListenerRef.current) {
                mainListenerRef.current();
                mainListenerRef.current = null;
            }

            // Unsubscribe from all document listeners
            generationListenersRef.current.forEach(unsubscribe => unsubscribe());
            generationListenersRef.current.clear();

            setListeningToRealtime(false);
        };
    }, [isAdmin]);

    const handleSort = (field) => {
        const newSortOrder = sortBy === field && sortOrder === 'desc' ? 'asc' : 'desc';
        setSortBy(field);
        setSortOrder(newSortOrder);
        fetchGenerations(1, field, newSortOrder);
    };

    // Refetch when filters change
    useEffect(() => {
        if (isAdmin && !isSearchMode) {
            fetchGenerations(1);
        }
    }, [userTypeFilter, showTestGenerations]);

    const handlePageChange = (newPage) => {
        if (isSearchMode) {
            // Handle pagination for search results
            handleActivitySearch(newPage);
        } else {
            fetchGenerations(newPage);
        }
    };

    const toggleRowExpansion = async (generationId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(generationId)) {
            newExpanded.delete(generationId);
        } else {
            newExpanded.add(generationId);
            
            // Always fetch detailed data when expanding (to get fresh data)
            if (!fetchingDetails.has(generationId)) {
                try {
                    setFetchingDetails(prev => new Set([...prev, generationId]));
                    console.log(`Fetching detailed data for generation ${generationId}...`);
                    const detailedData = await fetchGenerationDetails(generationId);
                    
                    // Update the detailed data (this will overwrite any existing data)
                    setDetailedGenerations(prev => new Map([...prev, [generationId, detailedData]]));
                    
                    // Also update the main generation in the list to keep data in sync
                    setGenerations(prev => prev.map(g => 
                        g.id === generationId ? detailedData : g
                    ));
                    
                    console.log(`Successfully fetched detailed data for generation ${generationId}`);
                } catch (error) {
                    console.error(`Error fetching detailed data for generation ${generationId}:`, error);
                } finally {
                    setFetchingDetails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(generationId);
                        return newSet;
                    });
                }
            }
        }
        setExpandedRows(newExpanded);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatDuration = (seconds) => {
        if (!seconds && seconds !== 0) return 'N/A';
        if (seconds < 60) {
            return `${seconds.toFixed(1)}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = (seconds % 60).toFixed(1);
        return `${minutes}m ${remainingSeconds}s`;
    };

    const formatAccountAge = (creationTime) => {
        if (!creationTime) return null;
        
        const created = new Date(creationTime);
        const now = new Date();
        const diffMs = now - created;
        
        const minutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const months = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30));
        const years = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365));
        
        if (years > 0) {
            return `${years} year${years === 1 ? '' : 's'} ago`;
        } else if (months > 0) {
            return `${months} month${months === 1 ? '' : 's'} ago`;
        } else if (days > 0) {
            return `${days} day${days === 1 ? '' : 's'} ago`;
        } else if (hours > 0) {
            return `${hours} hour${hours === 1 ? '' : 's'} ago`;
        } else if (minutes > 0) {
            return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
        } else {
            return 'Just now';
        }
    };

    const togglePromptExpansion = (generationId) => {
        const newExpandedPrompts = new Set(expandedPrompts);
        if (newExpandedPrompts.has(generationId)) {
            newExpandedPrompts.delete(generationId);
        } else {
            newExpandedPrompts.add(generationId);
        }
        setExpandedPrompts(newExpandedPrompts);
    };

    const getStatusBadgeClass = (status) => {
        if (!status) return 'unknown';
        if (status.includes('complete')) return 'completed';
        if (status.includes('error') || status.includes('failed')) return 'failed';
        if (status.includes('processing') || status.includes('generating') || 
            status.includes('creating') || status.includes('fetching') || 
            status.includes('storing')) return 'processing';
        return 'pending';
    };

    if (!user) {
        return (
            <div className="container admin-dashboard">
                <h2>Admin Dashboard</h2>
                <p>Please log in to access the admin dashboard.</p>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="container admin-dashboard">
                <h2>Admin Dashboard</h2>
                <p>Admin access required. Please contact support if you need access.</p>
                <Link to="/">&larr; Back to Home</Link>
            </div>
        );
    }

    return (
        <div className="container admin-dashboard">
            <div className="dashboard-header">
                <h1>Admin Dashboard</h1>
                <p>Monitor all map generations and manage users</p>
                <Link to="/" className="back-link">&larr; Back to Home</Link>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button 
                    className={`tab-button ${activeTab === 'generations' ? 'active' : ''}`}
                    onClick={() => setActiveTab('generations')}
                >
                    Map Generations
                </button>
                <button 
                    className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    User Management
                </button>
            </div>

            {/* Generations Tab */}
            {activeTab === 'generations' && (
                <>
                    {/* Real-time updates notification */}
                    {listeningToRealtime && newGenerationIds.size > 0 && (
                        <div className="realtime-notification">
                            <i className="ti ti-bell-ringing"></i>
                            <span>{newGenerationIds.size} new generation{newGenerationIds.size > 1 ? 's' : ''} added</span>
                        </div>
                    )}

                    {error && (
                        <div className="error-message">
                            <strong>Error:</strong> {error}
                            <button onClick={() => fetchGenerations()} className="retry-button">
                                Retry
                            </button>
                        </div>
                    )}

                    {isLoading ? (
                        <div className="loading-container">
                            <p>Loading generations...</p>
                        </div>
                    ) : (
                        <>
                            {/* Filter Card */}
                            <div className="filter-card">
                                <h3>Filter Generations</h3>
                                <div className="filter-card-content">
                                    <div className="filter-group">
                                        <label className="filter-toggle">
                                            <input
                                                type="checkbox"
                                                checked={showTestGenerations}
                                                onChange={(e) => setShowTestGenerations(e.target.checked)}
                                            />
                                            <span>Show test generations</span>
                                        </label>
                                    </div>
                                    
                                    <div className="filter-group">
                                        <div className="user-type-filter">
                                            <label htmlFor="userTypeFilter">User Type:</label>
                                            <select
                                                id="userTypeFilter"
                                                value={userTypeFilter}
                                                onChange={(e) => setUserTypeFilter(e.target.value)}
                                                className="filter-select"
                                            >
                                                <option value="all">All Users</option>
                                                <option value="admin">Admin Users</option>
                                                <option value="normal">Normal Users</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="dashboard-stats">
                                <div className="stat-card">
                                    <h3>Total Generations</h3>
                                    <p className="stat-number">{pagination.totalCount}</p>
                                </div>
                                <div className="stat-card">
                                    <h3>Current Page</h3>
                                    <p className="stat-number">{pagination.page} of {pagination.totalPages}</p>
                                </div>
                                <div className="stat-card">
                                    <h3>Showing</h3>
                                    <p className="stat-number">{filteredGenerations.length} items</p>
                                </div>
                                <div className="stat-card">
                                    <h3>Active Filter</h3>
                                    <p className="stat-number">
                                        {userTypeFilter === 'all' ? 'All' : 
                                         userTypeFilter === 'admin' ? 'Admin' : 'Normal'}
                                    </p>
                                </div>
                            </div>

                            <div className="search-section">
                                <div className="search-controls">
                                    <div className="search-type-selector">
                                        <label>Search by:</label>
                                        <select 
                                            value={searchType} 
                                            onChange={(e) => setSearchType(e.target.value)}
                                            className="search-type-select"
                                            disabled={isSearching}
                                        >
                                            <option value="activityName">Activity Name</option>
                                            <option value="activityType">Activity Type</option>
                                            <option value="dateRange">Date Range</option>
                                            <option value="power">Power (Watts)</option>
                                            <option value="distance">Distance (km)</option>
                                            <option value="elevation">Elevation (m)</option>
                                        </select>
                                    </div>
                                    
                                    <button
                                        onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                                        className="advanced-search-toggle"
                                        title={showAdvancedSearch ? "Hide advanced search" : "Show advanced search"}
                                    >
                                        <i className={`ti ti-${showAdvancedSearch ? 'chevron-up' : 'adjustments-horizontal'}`}></i>
                                        {showAdvancedSearch ? 'Hide' : 'Advanced'}
                                    </button>
                                </div>
                                
                                <div className="search-bar">
                                    {searchType === 'activityName' && (
                                        <input
                                            type="text"
                                            placeholder="Search by activity name (case-sensitive prefix)..."
                                            value={activitySearchTerm}
                                            onChange={(e) => setActivitySearchTerm(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleActivitySearch();
                                                }
                                            }}
                                            className="search-input"
                                            disabled={isSearching}
                                        />
                                    )}
                                    
                                    {searchType === 'activityType' && (
                                        <select
                                            value={activitySearchTerm}
                                            onChange={(e) => setActivitySearchTerm(e.target.value)}
                                            className="search-input"
                                            disabled={isSearching}
                                        >
                                            <option value="">Select activity type...</option>
                                            <option value="Run">Run</option>
                                            <option value="Ride">Ride</option>
                                            <option value="VirtualRide">Virtual Ride</option>
                                            <option value="EBikeRide">E-Bike Ride</option>
                                            <option value="Walk">Walk</option>
                                            <option value="Hike">Hike</option>
                                            <option value="Swim">Swim</option>
                                            <option value="AlpineSki">Alpine Ski</option>
                                            <option value="BackcountrySki">Backcountry Ski</option>
                                            <option value="NordicSki">Nordic Ski</option>
                                            <option value="Workout">Workout</option>
                                        </select>
                                    )}
                                    
                                    {searchType === 'dateRange' && (
                                        <>
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                className="search-input date-input"
                                                disabled={isSearching}
                                                placeholder="Start date"
                                            />
                                            <span className="date-separator">to</span>
                                            <input
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                className="search-input date-input"
                                                disabled={isSearching}
                                                placeholder="End date"
                                            />
                                        </>
                                    )}
                                    
                                    {['power', 'distance', 'elevation'].includes(searchType) && (
                                        <>
                                            <input
                                                type="number"
                                                placeholder={`Min ${searchType === 'power' ? 'watts' : searchType === 'distance' ? 'km' : 'meters'}`}
                                                value={minValue}
                                                onChange={(e) => setMinValue(e.target.value)}
                                                className="search-input number-input"
                                                disabled={isSearching}
                                                step={searchType === 'distance' ? '0.1' : '1'}
                                            />
                                            <span className="range-separator">-</span>
                                            <input
                                                type="number"
                                                placeholder={`Max ${searchType === 'power' ? 'watts' : searchType === 'distance' ? 'km' : 'meters'} (optional)`}
                                                value={maxValue}
                                                onChange={(e) => setMaxValue(e.target.value)}
                                                className="search-input number-input"
                                                disabled={isSearching}
                                                step={searchType === 'distance' ? '0.1' : '1'}
                                            />
                                        </>
                                    )}
                                    
                                    <button
                                        onClick={() => handleActivitySearch()}
                                        className="search-button"
                                        title="Search all activities"
                                        disabled={isSearching}
                                    >
                                        <i className="ti ti-search"></i>
                                        {isSearching ? 'Searching...' : 'Search'}
                                    </button>
                                    
                                    {isSearchMode && (
                                        <button
                                            onClick={clearActivitySearch}
                                            className="clear-search-button"
                                            title="Clear search and return to browse mode"
                                        >
                                            <i className="ti ti-x"></i>
                                            Clear
                                        </button>
                                    )}
                                </div>
                                
                                {showAdvancedSearch && (
                                    <div className="advanced-search-info">
                                        <p>
                                            <i className="ti ti-info-circle"></i>
                                            {searchType === 'activityName' && "Search finds activities starting with your search term (case-sensitive)"}
                                            {searchType === 'activityType' && "Filter by specific activity types from Strava"}
                                            {searchType === 'dateRange' && "Find activities within a specific date range"}
                                            {searchType === 'power' && "Search for activities by average power output"}
                                            {searchType === 'distance' && "Search for activities by distance covered"}
                                            {searchType === 'elevation' && "Search for activities by elevation gain"}
                                        </p>
                                    </div>
                                )}
                                

                                {isSearchMode && searchResults && (
                                    <p className="search-results-info" style={{ color: 'var(--Primary-600)', fontWeight: '500' }}>
                                        <i className="ti ti-search" style={{ marginRight: '5px' }}></i>
                                        {searchType === 'activityName' && `Activities starting with "${searchResults.searchTerm}"`}
                                        {searchType === 'activityType' && `${searchResults.searchTerm} activities`}
                                        {searchType === 'dateRange' && `Activities from ${startDate} to ${endDate}`}
                                        {searchType === 'power' && `Activities with power ${minValue}${maxValue ? `-${maxValue}` : '+'} watts`}
                                        {searchType === 'distance' && `Activities with distance ${minValue}${maxValue ? `-${maxValue}` : '+'} km`}
                                        {searchType === 'elevation' && `Activities with elevation ${minValue}${maxValue ? `-${maxValue}` : '+'} m`}
                                        : Found {searchResults.pagination.totalCount} total generation{searchResults.pagination.totalCount !== 1 ? 's' : ''}
                                    </p>
                                )}
                                {!isSearchMode && activitySearchTerm && (
                                    <p className="search-results-info">
                                        Filtering current page: {filteredGenerations.length} generation{filteredGenerations.length !== 1 ? 's' : ''} 
                                        {filteredGenerations.length < generations.length && ` out of ${generations.length}`}
                                        <br />
                                        <small style={{ color: 'var(--Gray-500)' }}>Click "Search All" to search all activities in the database</small>
                                    </p>
                                )}
                                {searchError && (
                                    <div className="search-error" style={{ color: 'var(--Error-500)', marginTop: '10px' }}>
                                        <i className="ti ti-alert-circle"></i> {searchError}
                                    </div>
                                )}
                            </div>

                            <div className="table-container">
                                <table className="generations-table">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th
                                                className={`sortable ${sortBy === 'createdAt' ? 'active' : ''}`}
                                                onClick={() => handleSort('createdAt')}
                                            >
                                                Created {sortBy === 'createdAt' && (sortOrder === 'desc' ? '↓' : '↑')}
                                            </th>
                                            <th
                                                className={`sortable ${sortBy === 'userId' ? 'active' : ''}`}
                                                onClick={() => handleSort('userId')}
                                            >
                                                User {sortBy === 'userId' && (sortOrder === 'desc' ? '↓' : '↑')}
                                            </th>
                                            <th>Bonuses</th>
                                            <th
                                                className={`sortable ${sortBy === 'status' ? 'active' : ''}`}
                                                onClick={() => handleSort('status')}
                                            >
                                                Status {sortBy === 'status' && (sortOrder === 'desc' ? '↓' : '↑')}
                                            </th>
                                            <th>Strava Activity</th>
                                            <th>Location</th>
                                            <th>Credits</th>
                                            <th>Duration</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredGenerations.map((generation) => {
                                            // Use detailed data if available, otherwise use list data
                                            const displayGeneration = detailedGenerations.has(generation.id) 
                                                ? detailedGenerations.get(generation.id) 
                                                : generation;
                                                
                                            return (
                                            <React.Fragment key={generation.id}>
                                                <tr className={`generation-row ${newGenerationIds.has(generation.id) ? 'new-generation' : ''}`}>
                                                    <td>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                                            <button
                                                                className="expand-button"
                                                                onClick={() => toggleRowExpansion(generation.id)}
                                                            >
                                                                {expandedRows.has(generation.id) ? '−' : '+'}
                                                            </button>
                                                            {generation.hasWatermark && (
                                                                <div 
                                                                    className="row-watermark-indicator" 
                                                                    title="Watermark detected on this generation"
                                                                >
                                                                    <i className="ti ti-photo-check"></i>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{formatDate(generation.createdAt)}</td>
                                                    <td>
                                                        <div className="user-info">
                                                            <div className="user-name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                {generation.isSystemTest ? (
                                                                    <span className="system-user">System Test User</span>
                                                                ) : (
                                                                    <>
                                                                        {generation.user?.stravaAthleteId ? (
                                                                            <a 
                                                                                href={`https://www.strava.com/athletes/${generation.user.stravaAthleteId}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{ 
                                                                                    color: 'inherit', 
                                                                                    textDecoration: 'none',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                                            >
                                                                                {generation.user?.displayName || generation.user?.email || 'Unknown'}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{generation.user?.displayName || generation.user?.email || 'Unknown'}</span>
                                                                        )}
                                                                        {generation.user?.isAdmin && <span className="admin-badge" style={{ marginLeft: '4px' }}>Admin</span>}
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="user-email">
                                                                {generation.isSystemTest ? 'Test Generation' : generation.user?.email}
                                                            </div>
                                                            {!generation.isSystemTest && generation.user?.creationTime && (
                                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                                                    Created: {formatAccountAge(generation.user.creationTime)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="bonus-info">
                                                            {generation.user?.bonusCredits ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    {generation.user.bonusCredits.email?.claimed && (
                                                                        <div className="bonus-item">
                                                                            <span className="bonus-badge email-bonus">
                                                                                <i className="ti ti-mail"></i>
                                                                                Email
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {generation.user.bonusCredits.watermarks?.totalCreditsEarned > 0 && (
                                                                        <div className="bonus-item">
                                                                            <span className="bonus-badge watermark-bonus">
                                                                                <i className="ti ti-photo-check"></i>
                                                                                Watermark ×{generation.user.bonusCredits.watermarks.totalCreditsEarned}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {generation.user.bonusCredits.stravaClub?.claimed && (
                                                                        <div className="bonus-item">
                                                                            <span className="bonus-badge strava-bonus">
                                                                                <i className="ti ti-users"></i>
                                                                                Strava Club
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {!generation.user.bonusCredits.email?.claimed && 
                                                                     generation.user.bonusCredits.watermarks?.totalCreditsEarned === 0 && 
                                                                     !generation.user.bonusCredits.stravaClub?.claimed && (
                                                                        <span style={{ color: '#999', fontSize: '12px' }}>None</span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                                                <span className={`status-badge ${getStatusBadgeClass(generation.status)}`}>
                                                                    {generation.status || 'Unknown'}
                                                                </span>
                                                                {generation.isSystemTest && (
                                                                    <span className="test-badge" title={`Test for parent: ${generation.parentGenerationId}`}>
                                                                        TEST
                                                                    </span>
                                                                )}
                                                                {generation.isRegeneration && (
                                                                    <span 
                                                                        className="regeneration-badge" 
                                                                        title={`Free retry ${generation.regenerationNumber || ''} of 3`}
                                                                    >
                                                                        FREE RETRY
                                                                    </span>
                                                                )}
                                                                {(displayGeneration.hasBadges || displayGeneration.compositeImageUrl) && (
                                                                    <span 
                                                                        className="badge-indicator" 
                                                                        title="This map has badges"
                                                                        style={{
                                                                            backgroundColor: '#8B5CF6',
                                                                            color: 'white',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '11px',
                                                                            fontWeight: '600',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px'
                                                                        }}
                                                                    >
                                                                        <i className="ti ti-award" style={{ fontSize: '12px' }}></i>
                                                                        BADGES
                                                                    </span>
                                                                )}
                                                                {generation.hasWatermark && (
                                                                    <span 
                                                                        className="watermark-badge" 
                                                                        title={`Watermark detected with ${Math.round((generation.watermarkConfidence || 0) * 100)}% confidence`}
                                                                    >
                                                                        <i className="ti ti-photo-check"></i>
                                                                        {generation.watermarkConfidence ? `${Math.round(generation.watermarkConfidence * 100)}%` : 'Detected'}
                                                                    </span>
                                                                )}
                                                                {generation.userFeedback && (
                                                                    <span 
                                                                        className={`feedback-badge ${generation.userFeedback}`}
                                                                        title={`User ${generation.userFeedback === 'like' ? 'liked' : 'disliked'} this generation`}
                                                                        style={{
                                                                            padding: '2px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '11px',
                                                                            fontWeight: '600',
                                                                            backgroundColor: generation.userFeedback === 'like' ? '#10b98120' : '#dc262620',
                                                                            color: generation.userFeedback === 'like' ? '#10b981' : '#dc2626',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px'
                                                                        }}
                                                                    >
                                                                        <i className={`ti ti-thumb-${generation.userFeedback === 'like' ? 'up' : 'down'}`} style={{ fontSize: '12px' }}></i>
                                                                        {generation.userFeedback === 'like' ? 'Liked' : 'Disliked'}
                                                                    </span>
                                                                )}
                                                                {generation.promptTemplateId && (
                                                                    <span 
                                                                        className="template-badge"
                                                                        title={`Template: ${templateNames.get(generation.promptTemplateId) || generation.promptTemplateId}`}
                                                                    >
                                                                        <i className="ti ti-palette"></i>
                                                                        {templateNames.get(generation.promptTemplateId) || generation.promptTemplateId}
                                                                    </span>
                                                                )}
                                                                {generation.styleName && (
                                                                    <span 
                                                                        className="style-badge"
                                                                        title={`Style: ${generation.styleName}`}
                                                                        style={{ marginLeft: '4px', fontSize: '10px', padding: '2px 6px', backgroundColor: '#d4e6ff', borderRadius: '3px' }}
                                                                    >
                                                                        {generation.styleName}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* Debug info for watermark detection */}
                                                            {generation.watermarkCheckPerformed && (
                                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                                    {generation.highestCombinedScore !== null && (
                                                                        <span title="Highest combined score from all analyzed photos">
                                                                            Max score: {Math.round(generation.highestCombinedScore * 100)}%
                                                                        </span>
                                                                    )}
                                                                    {!generation.hasWatermark && generation.watermarkedImageUrl && (
                                                                        <span style={{ marginLeft: '8px', color: '#f59e0b' }}>
                                                                            (Below threshold)
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="activity-info">
                                                            {generation.stravaStats?.activityName && generation.stravaId ? (
                                                                <div className="activity-name">
                                                                    <a 
                                                                        href={`https://www.strava.com/activities/${generation.stravaId}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ 
                                                                            color: 'inherit', 
                                                                            textDecoration: 'none',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                                    >
                                                                        {generation.stravaStats.activityName}
                                                                    </a>
                                                                </div>
                                                            ) : generation.stravaStats?.activityName ? (
                                                                <div className="activity-name">{generation.stravaStats.activityName}</div>
                                                            ) : null}
                                                            {generation.stravaId && (
                                                                <div className="activity-id">
                                                                    <a 
                                                                        href={`https://www.strava.com/activities/${generation.stravaId}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ 
                                                                            color: '#666', 
                                                                            textDecoration: 'none',
                                                                            fontSize: '12px'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                                    >
                                                                        ID: {generation.stravaId}
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {/* Badge display */}
                                                            {generation.stravaId && (
                                                                <div 
                                                                    className="badge-display"
                                                                >
                                                                    {loadingBadges.has(generation.id) ? (
                                                                        <span style={{ fontSize: '11px', color: '#666' }}>Loading badges...</span>
                                                                    ) : generationBadges.get(generation.id)?.length > 0 ? (
                                                                        generationBadges.get(generation.id).map((badge, index) => (
                                                                            <div
                                                                                key={index}
                                                                                className="badge-icon"
                                                                                title={`${badge.name}${generation.styleName ? ` (${generation.styleName} style)` : ''}`}
                                                                                onClick={() => setSelectedBadge({ ...badge, styleName: generation.styleName })}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                <img 
                                                                                    src={badge.imageUrl} 
                                                                                    alt={badge.name}
                                                                                />
                                                                            </div>
                                                                        ))
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="location-info" style={{ fontSize: '12px' }}>
                                                            {generation.stravaStats?.city || generation.stravaStats?.state || generation.stravaStats?.country ? (
                                                                <>
                                                                    <div style={{ fontWeight: '500' }}>
                                                                        {[generation.stravaStats.city, generation.stravaStats.state].filter(Boolean).join(', ')}
                                                                    </div>
                                                                    {generation.stravaStats.country && (
                                                                        <div style={{ color: '#666', fontSize: '11px' }}>{generation.stravaStats.country}</div>
                                                                    )}
                                                                    {generation.stravaStats.locationSource && (
                                                                        <div style={{ 
                                                                            color: '#888', 
                                                                            fontSize: '10px',
                                                                            fontStyle: 'italic',
                                                                            marginTop: '2px'
                                                                        }}>
                                                                            via {generation.stravaStats.locationSource.replace('_', ' ')}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span style={{ color: '#999' }}>—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {generation.creditSnapshot ? (
                                                            <div className="credit-info" style={{ fontSize: '12px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <span style={{ fontWeight: '600' }}>{generation.creditSnapshot.creditsUsed}/{generation.creditSnapshot.monthlyLimit}</span>
                                                                    <span style={{ color: '#666' }}>used</span>
                                                                </div>
                                                                <div style={{ color: generation.creditSnapshot.creditsRemaining === 0 ? '#dc2626' : '#10b981', fontSize: '11px' }}>
                                                                    {generation.creditSnapshot.creditsRemaining} left
                                                                </div>
                                                                {generation.creditSnapshot.bonusCredits > 0 && (
                                                                    <div style={{ color: '#7c3aed', fontSize: '11px', marginTop: '2px' }}>
                                                                        +{generation.creditSnapshot.bonusCredits} bonus
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td>{formatDuration(generation.generationDurationSeconds)}</td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <Link to={`/map/${generation.id}`} className="view-button">
                                                                View
                                                            </Link>
                                                            {!generation.isSystemTest && generations.some(g => g.parentGenerationId === generation.id) && (
                                                                <Link to={`/admin/compare/${generation.id}`} className="compare-button" target="_blank" rel="noopener noreferrer">
                                                                    Compare Tests
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandedRows.has(generation.id) && (
                                                    <tr className="expanded-row">
                                                        <td colSpan="9">
                                                            {fetchingDetails.has(generation.id) && (
                                                                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                                                    <i className="ti ti-loader" style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }}></i>
                                                                    Loading detailed information...
                                                                </div>
                                                            )}
                                                            <div className="expanded-content">
                                                                <div className="expanded-section">
                                                                    <h4>User Details</h4>
                                                                    <div className="detail-grid">
                                                                        <div><strong>UID:</strong> {displayGeneration.user?.uid || 'N/A'}</div>
                                                                        <div><strong>Created:</strong> {displayGeneration.user?.creationTime ? formatDate(displayGeneration.user.creationTime) : 'N/A'}</div>
                                                                        <div><strong>Last Sign In:</strong> {displayGeneration.user?.lastSignInTime ? formatDate(displayGeneration.user.lastSignInTime) : 'N/A'}</div>
                                                                        <div><strong>Admin:</strong> {displayGeneration.user?.isAdmin ? 'Yes' : 'No'}</div>
                                                                    </div>
                                                                </div>

                                                                <div className="expanded-section">
                                                                    <h4>Generation Details</h4>
                                                                    <div className="detail-grid">
                                                                        <div><strong>ID:</strong> {displayGeneration.id}</div>
                                                                        <div><strong>Started:</strong> {formatDate(displayGeneration.generationStartTime)}</div>
                                                                        <div><strong>Completed:</strong> {formatDate(displayGeneration.generationEndTime)}</div>
                                                                        <div><strong>Template:</strong> {displayGeneration.promptTemplateId || 'Custom'}</div>
                                                                        {displayGeneration.generationModel && (
                                                                            <div><strong>Model:</strong> {displayGeneration.generationModel}</div>
                                                                        )}
                                                                        {displayGeneration.userFeedback && (
                                                                            <div>
                                                                                <strong>User Feedback:</strong> 
                                                                                <span style={{ 
                                                                                    marginLeft: '8px',
                                                                                    padding: '4px 8px',
                                                                                    borderRadius: '4px',
                                                                                    backgroundColor: displayGeneration.userFeedback === 'like' ? '#10b98120' : '#dc262620',
                                                                                    color: displayGeneration.userFeedback === 'like' ? '#10b981' : '#dc2626'
                                                                                }}>
                                                                                    {displayGeneration.userFeedback === 'like' ? (
                                                                                        <><i className="ti ti-thumb-up"></i> Liked</>
                                                                                    ) : (
                                                                                        <><i className="ti ti-thumb-down"></i> Disliked</>
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {displayGeneration.error && (
                                                                        <div className="error-details">
                                                                            <strong>Error:</strong> {displayGeneration.error}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {(displayGeneration.finalPromptSentToOpenAI || displayGeneration.revisedPrompt) && (
                                                                    <div className="expanded-section">
                                                                        <h4 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                            <span>
                                                                                Prompts
                                                                                {displayGeneration.status && (displayGeneration.status.includes('error') || displayGeneration.status.includes('failed')) && (
                                                                                    <span style={{ marginLeft: '10px', fontSize: '12px', color: '#dc3545', fontWeight: 'normal' }}>
                                                                                        (Request failed after prompt was sent)
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                            <button
                                                                                className="toggle-prompt-button"
                                                                                onClick={() => togglePromptExpansion(generation.id)}
                                                                                title={expandedPrompts.has(generation.id) ? 'Hide prompts' : 'Show prompts'}
                                                                            >
                                                                                {expandedPrompts.has(generation.id) ? 'Hide' : 'Show'}
                                                                            </button>
                                                                        </h4>
                                                                        {expandedPrompts.has(generation.id) && (
                                                                            <div>
                                                                        {displayGeneration.finalPromptSentToOpenAI && (
                                                                            <div className="prompt-section">
                                                                                <div className="prompt-header">
                                                                                    <strong>Final Prompt Sent to OpenAI:</strong>
                                                                                    <button
                                                                                        className="copy-button"
                                                                                        onClick={() => {
                                                                                            navigator.clipboard.writeText(displayGeneration.finalPromptSentToOpenAI);
                                                                                            alert('Prompt copied to clipboard!');
                                                                                        }}
                                                                                        title="Copy to clipboard"
                                                                                    >
                                                                                        <i className="ti ti-copy"></i> Copy
                                                                                    </button>
                                                                                </div>
                                                                                <pre className="prompt-text">{displayGeneration.finalPromptSentToOpenAI}</pre>
                                                                            </div>
                                                                        )}
                                                                        {displayGeneration.revisedPrompt && (
                                                                            <div className="prompt-section" style={{ marginTop: '15px' }}>
                                                                                <div className="prompt-header">
                                                                                    <strong>Revised Prompt (from AI):</strong>
                                                                                    <button
                                                                                        className="copy-button"
                                                                                        onClick={() => {
                                                                                            navigator.clipboard.writeText(displayGeneration.revisedPrompt);
                                                                                            alert('Revised prompt copied to clipboard!');
                                                                                        }}
                                                                                        title="Copy to clipboard"
                                                                                    >
                                                                                        <i className="ti ti-copy"></i> Copy
                                                                                    </button>
                                                                                </div>
                                                                                <pre className="prompt-text">{displayGeneration.revisedPrompt}</pre>
                                                                            </div>
                                                                        )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Show message if error state but no prompt data */}
                                                                {displayGeneration.status && (displayGeneration.status.includes('error') || displayGeneration.status.includes('failed')) && 
                                                                 !displayGeneration.finalPromptSentToOpenAI && !displayGeneration.revisedPrompt && (
                                                                    <div className="expanded-section">
                                                                        <h4>Prompts</h4>
                                                                        <div style={{ 
                                                                            padding: '12px', 
                                                                            backgroundColor: '#fef2f2', 
                                                                            border: '1px solid #fecaca',
                                                                            borderRadius: '6px',
                                                                            color: '#991b1b',
                                                                            fontSize: '14px'
                                                                        }}>
                                                                            <i className="ti ti-alert-circle" style={{ marginRight: '8px' }}></i>
                                                                            No prompt data available - the request likely failed before reaching OpenAI
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {displayGeneration.stravaStats && (
                                                                    <div className="expanded-section">
                                                                        <h4>Activity Stats</h4>
                                                                        <div className="detail-grid">
                                                                            <div><strong>Type:</strong> {displayGeneration.stravaStats.activityType || 'N/A'}</div>
                                                                            <div><strong>Distance:</strong> {displayGeneration.stravaStats.distance ? `${(displayGeneration.stravaStats.distance / 1000).toFixed(2)} km` : 'N/A'}</div>
                                                                            <div><strong>Moving Time:</strong> {displayGeneration.stravaStats.movingTime ? formatDuration(displayGeneration.stravaStats.movingTime) : 'N/A'}</div>
                                                                            <div><strong>Elevation:</strong> {displayGeneration.stravaStats.elevationGain ? `${displayGeneration.stravaStats.elevationGain}m` : 'N/A'}</div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {(generation.hasWatermark || (generation.watermarkCheckPerformed && generation.watermarkedImageUrl)) && (
                                                                    <div className="expanded-section watermark-section">
                                                                        <h4>
                                                                            <i className="ti ti-photo-check" style={{ marginRight: '8px', color: generation.hasWatermark ? '#10b981' : '#f59e0b' }}></i>
                                                                            Watermark Detection {!generation.hasWatermark && '(Debug)'}
                                                                        </h4>
                                                                        <div className="watermark-content">
                                                                            <div className="detail-grid">
                                                                                <div><strong>Status:</strong> 
                                                                    {generation.hasWatermark ? (
                                                                        <span style={{ color: '#10b981' }}>✓ Watermark Detected</span>
                                                                    ) : (
                                                                        <span style={{ color: '#f59e0b' }}>⚠ Below Threshold</span>
                                                                    )}
                                                                </div>
                                                                                <div><strong>Check Date:</strong> {generation.watermarkCheckTimestamp ? formatDate(generation.watermarkCheckTimestamp) : 'N/A'}</div>
                                                                                <div><strong>Confidence:</strong> {generation.watermarkConfidence ? `${Math.round(generation.watermarkConfidence * 100)}%` : 'N/A'}</div>
                                                                                <div><strong>Credit Applied:</strong> {generation.hasWatermark ? 'Yes' : 'No'}</div>
                                                                {generation.highestCombinedScore !== null && (
                                                                    <div><strong>Highest Score:</strong> {Math.round(generation.highestCombinedScore * 100)}%</div>
                                                                )}
                                                                                {generation.watermarkedImageResolution && (
                                                                                    <div><strong>Image Resolution:</strong> {generation.watermarkedImageResolution.width} × {generation.watermarkedImageResolution.height}</div>
                                                                                )}
                                                                            </div>
                                                                            {generation.watermarkedImageUrl && (
                                                                                <div className="watermark-image-section">
                                                                                    <h5>Detected on Strava:</h5>
                                                                                    <div className="watermark-image-container">
                                                                                        <img 
                                                                                            src={generation.watermarkedImageUrl} 
                                                                                            alt="Watermarked map on Strava" 
                                                                                            className="thumbnail"
                                                                                        />
                                                                                        <div className="watermark-image-caption">
                                                                                            Best photo with watermark detected
                                                                                            {generation.watermarkedImageResolution && (
                                                                                                <span className="resolution-info">
                                                                                                    {' '}• {generation.watermarkedImageResolution.width} × {generation.watermarkedImageResolution.height}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {displayGeneration.contourImageUrl && (
                                                                    <div className="expanded-section">
                                                                        <h4>Contour Preprocessing</h4>
                                                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                                                            <div style={{ maxWidth: '280px' }}>
                                                                                <img
                                                                                    src={displayGeneration.contourImageUrl}
                                                                                    alt="Contour preprocessing output"
                                                                                    className="thumbnail"
                                                                                    style={{ width: '100%', maxWidth: '280px' }}
                                                                                />
                                                                            </div>
                                                                            {displayGeneration.contourPrompt && (
                                                                                <div style={{ flex: '1 1 240px', minWidth: '220px' }}>
                                                                                    <strong>Flux Prompt</strong>
                                                                                    <pre style={{
                                                                                        background: '#f8fafc',
                                                                                        padding: '12px',
                                                                                        borderRadius: '6px',
                                                                                        border: '1px solid #e2e8f0',
                                                                                        whiteSpace: 'pre-wrap',
                                                                                        wordBreak: 'break-word',
                                                                                        fontSize: '12px',
                                                                                        maxHeight: '200px',
                                                                                        overflow: 'auto'
                                                                                    }}>{displayGeneration.contourPrompt}</pre>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {(displayGeneration.generatedImageUrl || displayGeneration.originalImageUrl) && (
                                                                    <div className="expanded-section">
                                                                        <h4>Images</h4>
                                                                        <div className="image-grid">
                                                                            {displayGeneration.originalImageUrl && displayGeneration.originalImageUrl !== "[Available on expand]" && (
                                                                                <div className="image-item">
                                                                                    <strong>Original Map:</strong>
                                                                                    <img src={displayGeneration.originalImageUrl} alt="Original map" className="thumbnail" />
                                                                                    <button
                                                                                        className="download-button"
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const response = await fetch(displayGeneration.originalImageUrl);
                                                                                                const blob = await response.blob();
                                                                                                const url = window.URL.createObjectURL(blob);
                                                                                                const a = document.createElement('a');
                                                                                                a.href = url;
                                                                                                a.download = `original_map_${generation.requestId}.png`;
                                                                                                document.body.appendChild(a);
                                                                                                a.click();
                                                                                                window.URL.revokeObjectURL(url);
                                                                                                document.body.removeChild(a);
                                                                                            } catch (error) {
                                                                                                console.error('Error downloading original image:', error);
                                                                                                alert('Failed to download image');
                                                                                            }
                                                                                        }}
                                                                                        title="Download original map"
                                                                                    >
                                                                                        <i className="ti ti-download"></i> Download
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                            {displayGeneration.generatedImageUrl && displayGeneration.generatedImageUrl !== "[Available on expand]" && (
                                                                                <div className="image-item">
                                                                                    <strong>Generated Map{displayGeneration.compositeImageUrl && ' (with badges)'}:</strong>
                                                                                    <img src={displayGeneration.compositeImageUrl || displayGeneration.generatedImageUrl} alt="Generated map" className="thumbnail" />
                                                                                    <button
                                                                                        className="download-button"
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const imageUrl = displayGeneration.compositeImageUrl || displayGeneration.generatedImageUrl;
                                                                                                const response = await fetch(imageUrl);
                                                                                                const blob = await response.blob();
                                                                                                const url = window.URL.createObjectURL(blob);
                                                                                                const a = document.createElement('a');
                                                                                                a.href = url;
                                                                                                a.download = `generated_map_${generation.requestId}${displayGeneration.compositeImageUrl ? '_with_badges' : ''}.png`;
                                                                                                document.body.appendChild(a);
                                                                                                a.click();
                                                                                                window.URL.revokeObjectURL(url);
                                                                                                document.body.removeChild(a);
                                                                                            } catch (error) {
                                                                                                console.error('Error downloading generated image:', error);
                                                                                                alert('Failed to download image');
                                                                                            }
                                                                                        }}
                                                                                        title="Download generated map"
                                                                                    >
                                                                                        <i className="ti ti-download"></i> Download
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {pagination.totalPages > 1 && (
                                <div className="pagination">
                                    <button 
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={!pagination.hasPreviousPage}
                                        className="pagination-button"
                                    >
                                        Previous
                                    </button>
                                    <span className="pagination-info">
                                        Page {pagination.page} of {pagination.totalPages}
                                    </span>
                                    <button 
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={!pagination.hasNextPage}
                                        className="pagination-button"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* User Management Tab */}
            {activeTab === 'users' && (
                <div className="user-management-section">
                    <div className="user-search">
                        <h3>Search Users</h3>
                        <div className="search-controls">
                            <input
                                type="text"
                                placeholder="Search by email or name..."
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleUserSearch()}
                                className="search-input"
                            />
                            <button 
                                onClick={handleUserSearch}
                                disabled={isSearchingUsers || userSearchTerm.trim().length < 2}
                                className="search-button"
                            >
                                {isSearchingUsers ? 'Searching...' : 'Search'}
                            </button>
                        </div>
                        
                        {userSearchError && (
                            <div className="error-message">
                                {userSearchError}
                            </div>
                        )}
                    </div>

                    {/* Temporary Reset All Credits Button */}
                    {isAdmin && (
                        <div className="admin-actions-section">
                            <h3>Admin Actions</h3>
                            <div className="danger-zone-actions">
                                <button
                                    className="reset-all-credits-button"
                                    onClick={() => {
                                        setShowResetModal(true);
                                        setResetConfirmText('');
                                        setResetResult(null);
                                    }}
                                    disabled={isResettingCredits}
                                >
                                    <i className="ti ti-alert-triangle"></i>
                                    Reset All User Credits (Temporary)
                                </button>
                                <p className="action-description">
                                    This will reset all users' credit usage to zero. Earned credits and admin overrides will be preserved.
                                </p>
                            </div>
                        </div>
                    )}

                    {searchedUsers.length > 0 && (
                        <div className="users-table-container">
                            <h3>Search Results ({searchedUsers.length} users)</h3>
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>User Info</th>
                                        <th>Account Status</th>
                                        <th>Current Usage</th>
                                        <th>Monthly Limit</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchedUsers.map((searchedUser) => (
                                        <tr key={searchedUser.uid}>
                                            <td>
                                                <div className="user-info">
                                                    <div className="user-name">
                                                        {searchedUser.displayName || 'No display name'}
                                                        {searchedUser.isAdmin && <span className="admin-badge">Admin</span>}
                                                    </div>
                                                    <div className="user-email">{searchedUser.email}</div>
                                                    <div className="user-uid">{searchedUser.uid}</div>
                                                    {searchedUser.stravaAthleteId && (
                                                        <div className="user-strava">
                                                            <a 
                                                                href={`https://www.strava.com/athletes/${searchedUser.stravaAthleteId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#FC4C02', textDecoration: 'none', fontSize: '12px' }}
                                                            >
                                                                <i className="ti ti-brand-strava" style={{ marginRight: '4px' }}></i>
                                                                Strava Profile
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="account-status">
                                                    <div className={`status-indicator ${searchedUser.disabled ? 'disabled' : 'active'}`}>
                                                        {searchedUser.disabled ? 'Disabled' : 'Active'}
                                                    </div>
                                                    <div className="verification-status">
                                                        Email: {searchedUser.emailVerified ? 'Verified' : 'Unverified'}
                                                    </div>
                                                    <div className="account-dates">
                                                        <div>Created: {formatDate(searchedUser.creationTime)}</div>
                                                        <div>Last Sign In: {formatDate(searchedUser.lastSignInTime)}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="usage-info">
                                                    <div className="current-count">
                                                        {searchedUser.monthlyGenerations.currentCount} generations
                                                    </div>
                                                    <div className="month-info">
                                                        This month: {searchedUser.monthlyGenerations.month || 'Not set'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="limit-info">
                                                    <div className="effective-limit">
                                                        <strong>Current: {searchedUser.monthlyGenerations.effectiveLimit}</strong>
                                                    </div>
                                                    <div className="limit-source">
                                                        {searchedUser.monthlyGenerations.customLimit !== null 
                                                            ? `Custom limit (Default: ${searchedUser.monthlyGenerations.defaultLimit})`
                                                            : `Default limit`
                                                        }
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="user-actions">
                                                    <UserLimitEditor
                                                        userId={searchedUser.uid}
                                                        currentLimit={searchedUser.monthlyGenerations.customLimit}
                                                        defaultLimit={searchedUser.monthlyGenerations.defaultLimit}
                                                        onUpdateLimit={handleUpdateUserLimit}
                                                        isUpdating={updatingUserId === searchedUser.uid}
                                                    />
                                                    {isAdmin && !searchedUser.isAdmin && (
                                                        <button
                                                            className="delete-user-button"
                                                            onClick={() => {
                                                                setDeletingUser(searchedUser);
                                                                setShowDeleteModal(true);
                                                                setDeleteConfirmText('');
                                                            }}
                                                            disabled={isDeletingUser}
                                                            title="Delete user account"
                                                        >
                                                            <i className="ti ti-trash"></i> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Delete User Confirmation Modal */}
            {showDeleteModal && deletingUser && (
                <div className="modal-overlay" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowDeleteModal(false);
                        setDeleteConfirmText('');
                    }
                }}>
                    <div className="modal-content delete-modal">
                        <h2>Delete User Account</h2>
                        
                        <div className="warning-banner">
                            <i className="ti ti-alert-triangle"></i>
                            <p>This action cannot be undone!</p>
                        </div>

                        <div className="deletion-details">
                            <h4>You are about to delete:</h4>
                            <div className="user-info-modal">
                                <p><strong>Name:</strong> {deletingUser.displayName || 'No display name'}</p>
                                <p><strong>Email:</strong> {deletingUser.email}</p>
                                <p><strong>UID:</strong> {deletingUser.uid}</p>
                                {deletingUser.monthlyGenerations && (
                                    <p><strong>Current Usage:</strong> {deletingUser.monthlyGenerations.currentCount} generations this month</p>
                                )}
                            </div>

                            <h4>What will be deleted:</h4>
                            <ul>
                                <li>All generated maps</li>
                                <li>Personal information (name, email)</li>
                                <li>Activity data and preferences</li>
                                <li>The user's authentication account</li>
                            </ul>

                            <h4>What will be preserved:</h4>
                            <ul>
                                <li>Credit usage history</li>
                                <li>Monthly generation counts</li>
                                <li>Claimed reward records</li>
                            </ul>

                            <div className="info-text">
                                <i className="ti ti-info-circle"></i>
                                If this user signs up again with the same Strava account, their credit history will be restored.
                            </div>
                        </div>

                        <div className="confirmation-input">
                            <label htmlFor="confirm-text">
                                Type <strong>DELETE</strong> to confirm:
                            </label>
                            <input
                                id="confirm-text"
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder="Type DELETE to confirm"
                                autoComplete="off"
                            />
                        </div>

                        <div className="modal-actions">
                            <button
                                className="cancel-button"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeletingUser(null);
                                    setDeleteConfirmText('');
                                }}
                                disabled={isDeletingUser}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-delete-button"
                                onClick={handleDeleteUser}
                                disabled={deleteConfirmText !== 'DELETE' || isDeletingUser}
                            >
                                {isDeletingUser ? 'Deleting...' : 'Delete User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset All Credits Modal */}
            {showResetModal && (
                <div className="modal-overlay" onClick={(e) => {
                    if (e.target === e.currentTarget && !isResettingCredits) {
                        setShowResetModal(false);
                        setResetConfirmText('');
                        setResetResult(null);
                    }
                }}>
                    <div className="modal-content reset-modal">
                        {!resetResult ? (
                            <>
                                <h2>Reset All User Credits</h2>
                                
                                <div className="warning-banner">
                                    <i className="ti ti-alert-triangle"></i>
                                    <p>This will affect ALL users in the system!</p>
                                </div>

                                <div className="reset-details">
                                    <h4>What this will do:</h4>
                                    <ul>
                                        <li>Reset all users' credit usage count to 0</li>
                                        <li>Keep earned credits intact (from email registration, sharing, etc.)</li>
                                        <li>Keep admin overrides intact</li>
                                        <li>Allow all users to generate maps again up to their limit</li>
                                    </ul>

                                    <div className="info-text">
                                        <i className="ti ti-info-circle"></i>
                                        This is a temporary admin function. Use with caution.
                                    </div>
                                </div>

                                <div className="confirmation-input">
                                    <label htmlFor="reset-confirm-text">
                                        Type <strong>RESET ALL</strong> to confirm:
                                    </label>
                                    <input
                                        id="reset-confirm-text"
                                        type="text"
                                        value={resetConfirmText}
                                        onChange={(e) => setResetConfirmText(e.target.value)}
                                        placeholder="Type RESET ALL to confirm"
                                        autoComplete="off"
                                        disabled={isResettingCredits}
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button
                                        className="cancel-button"
                                        onClick={() => {
                                            setShowResetModal(false);
                                            setResetConfirmText('');
                                            setResetResult(null);
                                        }}
                                        disabled={isResettingCredits}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="confirm-reset-button"
                                        onClick={handleResetAllCredits}
                                        disabled={resetConfirmText !== 'RESET ALL' || isResettingCredits}
                                    >
                                        {isResettingCredits ? 'Resetting...' : 'Reset All Credits'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2>Reset Complete</h2>
                                
                                <div className="success-banner">
                                    <i className="ti ti-circle-check"></i>
                                    <p>Credits have been reset successfully!</p>
                                </div>

                                <div className="reset-summary">
                                    <h4>Summary:</h4>
                                    <div className="summary-stats">
                                        <div className="stat-item">
                                            <span className="stat-label">Total Users:</span>
                                            <span className="stat-value">{resetResult.totalUsers}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Credits Reset:</span>
                                            <span className="stat-value">{resetResult.updatedCount}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Already at Zero:</span>
                                            <span className="stat-value">{resetResult.skippedCount}</span>
                                        </div>
                                        {resetResult.errorCount > 0 && (
                                            <div className="stat-item error">
                                                <span className="stat-label">Errors:</span>
                                                <span className="stat-value">{resetResult.errorCount}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button
                                        className="close-button"
                                        onClick={() => {
                                            setShowResetModal(false);
                                            setResetConfirmText('');
                                            setResetResult(null);
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Badge Overlay */}
            {selectedBadge && (
                <div className="badge-overlay" onClick={() => setSelectedBadge(null)}>
                    <div className="badge-overlay-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="badge-overlay-close" 
                            onClick={() => setSelectedBadge(null)}
                        >
                            <i className="ti ti-x"></i>
                        </button>
                        
                        <div className="badge-overlay-image">
                            <img src={selectedBadge.imageUrl} alt={selectedBadge.name} />
                        </div>
                        
                        <div className="badge-overlay-details">
                            <h3>{selectedBadge.name}</h3>
                            
                            {selectedBadge.styleName && (
                                <div className="badge-detail-row">
                                    <span className="badge-detail-label">Style:</span>
                                    <span className="badge-detail-value">
                                        {selectedBadge.styleName}
                                        {/* Check if styleVersions exists and has any style-specific versions */}
                                        {selectedBadge.styleVersions && Object.keys(selectedBadge.styleVersions).length > 0 && (
                                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#666' }}>
                                                {Object.keys(selectedBadge.styleVersions).includes(selectedBadge.styleName) 
                                                    ? '(style-specific version)' 
                                                    : '(default version)'}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                            
                            <div className="badge-detail-row">
                                <span className="badge-detail-label">Type:</span>
                                <span className="badge-detail-value">
                                    {selectedBadge.conditionType === 'distance' ? 'Distance' : 'Location'}
                                </span>
                            </div>
                            
                            {selectedBadge.conditionType === 'distance' && selectedBadge.distanceThreshold && (
                                <>
                                    <div className="badge-detail-row">
                                        <span className="badge-detail-label">Distance Threshold:</span>
                                        <span className="badge-detail-value">
                                            {(selectedBadge.distanceThreshold / 1000).toFixed(1)} km
                                        </span>
                                    </div>
                                    {selectedBadge.activityType && (
                                        <div className="badge-detail-row">
                                            <span className="badge-detail-label">Activity Type:</span>
                                            <span className="badge-detail-value">
                                                {selectedBadge.activityType === 'ride' ? 'Rides Only' : 'Runs Only'}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {selectedBadge.conditionType === 'location' && (
                                <>
                                    {selectedBadge.locationCity && (
                                        <div className="badge-detail-row">
                                            <span className="badge-detail-label">City:</span>
                                            <span className="badge-detail-value">{selectedBadge.locationCity}</span>
                                        </div>
                                    )}
                                    {selectedBadge.locationState && (
                                        <div className="badge-detail-row">
                                            <span className="badge-detail-label">State:</span>
                                            <span className="badge-detail-value">{selectedBadge.locationState}</span>
                                        </div>
                                    )}
                                    {selectedBadge.locationCountry && (
                                        <div className="badge-detail-row">
                                            <span className="badge-detail-label">Country:</span>
                                            <span className="badge-detail-value">{selectedBadge.locationCountry}</span>
                                        </div>
                                    )}
                                </>
                            )}
                            
                            <div className="badge-detail-row">
                                <span className="badge-detail-label">Size:</span>
                                <span className="badge-detail-value">{selectedBadge.size || 10}%</span>
                            </div>
                            
                            {/* Available style versions info */}
                            {selectedBadge.styleVersions && Object.keys(selectedBadge.styleVersions).length > 0 && (
                                <div className="badge-detail-row">
                                    <span className="badge-detail-label">Available Styles:</span>
                                    <span className="badge-detail-value" style={{ fontSize: '12px' }}>
                                        {Object.keys(selectedBadge.styleVersions).join(', ')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard; 
